import type { Group } from '@/api/groups';
import type { Lesson, LessonRange, UpdateLessonPayload } from '@/api/lessons';
import type { Subject } from '@/api/subjects';
import type { Teacher } from '@/api/teachers';
import {
  addSchoolDays,
  schoolDateKey,
  schoolDayFromDateKey,
  schoolInstantFromDateMinutes,
} from '@/components/schedule/schoolTime';
import { resolveLocalized } from '@/i18n/localized';

export interface LessonsSearch {
  date?: string;
  q?: string;
  subject?: string;
  teacher?: string;
  type?: Lesson['audience_kind'];
  status?: 'scheduled' | 'cancelled' | 'in_progress' | 'completed';
  cancelled?: true;
}

export interface LessonRegistryLookups {
  subjectsById: ReadonlyMap<string, Subject>;
  groupsById: ReadonlyMap<string, Group>;
  teachersById: ReadonlyMap<string, Teacher>;
}

function nonemptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
}

export function parseLessonsSearch(search: Record<string, unknown>): LessonsSearch {
  const date = nonemptyString(search.date);
  const type =
    search.type === 'individual' || search.type === 'mini_group'
      ? 'mini_group'
      : search.type === 'group'
        ? 'group'
        : undefined;
  const status =
    search.status === 'scheduled' ||
    search.status === 'cancelled' ||
    search.status === 'in_progress' ||
    search.status === 'completed'
      ? search.status
      : undefined;
  return {
    ...(date && schoolDayFromDateKey(date) ? { date } : {}),
    ...(nonemptyString(search.q) ? { q: nonemptyString(search.q) } : {}),
    ...(nonemptyString(search.subject) ? { subject: nonemptyString(search.subject) } : {}),
    ...(nonemptyString(search.teacher) ? { teacher: nonemptyString(search.teacher) } : {}),
    ...(type ? { type } : {}),
    ...(status ? { status } : {}),
    ...(search.cancelled === true || search.cancelled === 'true' ? { cancelled: true } : {}),
  };
}

export function lessonDayRange(dateKey: string, includeArchived = false): LessonRange {
  const start = schoolDayFromDateKey(dateKey);
  if (!start) throw new RangeError('Lesson date must use YYYY-MM-DD');
  return {
    dateFrom: start.toISOString(),
    dateTo: addSchoolDays(start, 1).toISOString(),
    limit: 100,
    includeArchived,
    includeNotes: false,
  };
}

export function isLessonStarted(lesson: Lesson, now = new Date()): boolean {
  return new Date(lesson.starts_at).getTime() <= now.getTime();
}

export function isLessonEditable(lesson: Lesson, now = new Date()): boolean {
  return (
    !lesson.group_subject_suspended_at &&
    lesson.archived_at === null &&
    !isLessonStarted(lesson, now) &&
    (lesson.audience_kind !== 'mini_group' ||
      (lesson.status === 'draft' && !lesson.attendance_confirmed_at))
  );
}

function timeMinutes(value: string): number {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!match) throw new RangeError('Lesson time must use HH:mm');
  return Number(match[1]) * 60 + Number(match[2]);
}

export function timePatch(lesson: Lesson, value: string): UpdateLessonPayload {
  const dateKey = schoolDateKey(lesson.starts_at);
  if (!dateKey) throw new RangeError('Lesson start must be a valid instant');
  const startsAt = schoolInstantFromDateMinutes(dateKey, timeMinutes(value));
  if (!startsAt) throw new RangeError('Lesson time is invalid');
  const duration = new Date(lesson.ends_at).getTime() - new Date(lesson.starts_at).getTime();
  return {
    starts_at: startsAt.toISOString(),
    ends_at: new Date(startsAt.getTime() + duration).toISOString(),
  };
}

export function durationPatch(lesson: Lesson, minutes: number): UpdateLessonPayload {
  if (!Number.isInteger(minutes) || minutes <= 0) {
    throw new RangeError('Lesson duration must be a positive whole number');
  }
  const start = new Date(lesson.starts_at);
  if (Number.isNaN(start.getTime())) throw new RangeError('Lesson start must be valid');
  return { ends_at: new Date(start.getTime() + minutes * 60_000).toISOString() };
}

function lessonStatus(lesson: Lesson, now: Date): NonNullable<LessonsSearch['status']> {
  if (lesson.archived_at !== null) return 'cancelled';
  if (new Date(lesson.ends_at).getTime() <= now.getTime()) return 'completed';
  if (isLessonStarted(lesson, now)) return 'in_progress';
  return 'scheduled';
}

function namesForLesson(
  lesson: Lesson,
  lookups: LessonRegistryLookups,
  language: string,
): { subject: string; group: string; audience: string; searchable: string } {
  const subject = lesson.subject_name
    ? resolveLocalized(lesson.subject_name, language)
    : resolveLocalized(lookups.subjectsById.get(lesson.subject_id ?? '')?.name ?? {}, language);
  const group = lesson.group_name
    ? resolveLocalized(lesson.group_name, language)
    : resolveLocalized(lookups.groupsById.get(lesson.group_id)?.name ?? {}, language);
  const teacherNames = [
    lesson.primary_teacher_name,
    lesson.effective_teacher_name,
    lesson.substitute_teacher_name,
    lesson.teacher_name,
    lookups.teachersById.get(lesson.primary_teacher_id)?.full_name,
    lookups.teachersById.get(lesson.effective_teacher_id)?.full_name,
  ].filter((value): value is string => Boolean(value));
  const audience =
    lesson.audience_kind === 'individual' ? (lesson.assigned_student_name ?? '') : group;
  return {
    subject,
    group,
    audience,
    searchable: [subject, group, audience, ...teacherNames].join(' '),
  };
}

export function filterAndSortLessons(
  lessons: Lesson[],
  search: LessonsSearch,
  lookups: LessonRegistryLookups,
  language: string,
  now = new Date(),
): Lesson[] {
  const query = search.q?.toLocaleLowerCase(language);
  const labels = new Map(
    lessons.map((lesson) => [lesson.id, namesForLesson(lesson, lookups, language)]),
  );
  return lessons
    .filter((lesson) => {
      if (search.subject && lesson.subject_id !== search.subject) return false;
      if (search.teacher && lesson.effective_teacher_id !== search.teacher) return false;
      if (search.type === 'mini_group') {
        if (lesson.audience_kind !== 'individual' && lesson.audience_kind !== 'mini_group')
          return false;
      } else if (search.type && lesson.audience_kind !== search.type) return false;
      if (search.status && lessonStatus(lesson, now) !== search.status) return false;
      if (query && !labels.get(lesson.id)!.searchable.toLocaleLowerCase(language).includes(query)) {
        return false;
      }
      return true;
    })
    .sort((left, right) => {
      const time = new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime();
      if (time !== 0) return time;
      const leftLabels = labels.get(left.id)!;
      const rightLabels = labels.get(right.id)!;
      const subject = leftLabels.subject.localeCompare(rightLabels.subject, language);
      if (subject !== 0) return subject;
      const audience = leftLabels.audience.localeCompare(rightLabels.audience, language);
      return audience || left.id.localeCompare(right.id);
    });
}
